import React, { useMemo } from "react";
import { Tooltip as MuiTooltip } from "@mui/material";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatDateLabel = (value) => {
  if (typeof value !== "string") return value;
  if (!value.includes("-")) return value;
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  const [year, month, day] = parts;
  return `${day}-${month}-${year.slice(-2)}`;
};

const buildStudyMap = (entries = []) => {
  const map = new Map();
  let referenceYear = new Date().getFullYear();

  entries.forEach((item, index) => {
    const seconds = item?.time_spent || 0;
    const hours = seconds / 3600;
    if (!item?.date) {
      return;
    }
    map.set(item.date, hours);
    if (index === 0) {
      const [year] = item.date.split("-");
      if (year) {
        referenceYear = Number(year);
      }
    }
  });

  return { map, referenceYear };
};

const YearOverviewCard = ({ yearTimeSpent = [] }) => {
  const { map: studyMap, referenceYear } = useMemo(
    () => buildStudyMap(yearTimeSpent),
    [yearTimeSpent]
  );

  return (
    <div className="card-styled-wrapper year-overview-card">
      <div className="year-overview-card__content">
        <div className="year-overview-header">
          <p className="year-overview-title">Year-Level Overview</p>
        </div>

        <div className="year-overview-scroll custom-scrollbar">
          <div className="year-overview-month-row">
            {MONTH_LABELS.map((month) => (
              <span key={month} className="year-overview-month-label">
                {month}
              </span>
            ))}
          </div>

          <div className="year-overview-grid-wrapper">
            <div className="year-overview-weekday-column">
              {WEEKDAY_LABELS.map((weekday) => (
                <span key={weekday} className="year-overview-weekday-label">
                  {weekday}
                </span>
              ))}
            </div>

            <div className="year-overview-weeks custom-scrollbar">
              {Array.from({ length: 53 }).map((_, weekIndex) => (
                <div key={weekIndex} className="year-overview-week">
                  {Array.from({ length: 7 }).map((__, dayIndex) => {
                    const startDate = new Date(referenceYear, 0, 1);
                    const startOffset = startDate.getDay();
                    const dayOffset = weekIndex * 7 + dayIndex - startOffset;

                    const cellDate = new Date(referenceYear, 0, 1);
                    cellDate.setDate(cellDate.getDate() + dayOffset);

                    const dateKey = `${cellDate.getFullYear()}-${String(
                      cellDate.getMonth() + 1
                    ).padStart(2, "0")}-${String(cellDate.getDate()).padStart(
                      2,
                      "0"
                    )}`;

                    const hours = studyMap.get(dateKey) || 0;

                    let color = "#F4F6F8";
                    if (hours > 2) color = "#ffb300";
                    else if (hours > 1) color = "#ffc107";
                    else if (hours > 0) color = "#fff8e6";

                    const isCurrentYear =
                      cellDate.getFullYear() === referenceYear;

                    const hasStudy = isCurrentYear && hours > 0;

                    return (
                      <MuiTooltip
                        key={dayIndex}
                        title={
                          isCurrentYear
                            ? `${formatDateLabel(dateKey)}: ${hours.toFixed(
                                1
                              )} hours`
                            : ""
                        }
                        arrow
                        placement="top"
                      >
                        <div
                          className="year-overview-cell"
                          style={{
                            backgroundColor: isCurrentYear
                              ? color
                              : "transparent",
                            border: isCurrentYear
                              ? "1px solid rgba(145, 158, 171, 0.12)"
                              : "none",
                            cursor: hasStudy ? "pointer" : "default",
                          }}
                        />
                      </MuiTooltip>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="year-overview-legend">
          <span className="year-overview-legend-label">Less</span>
          <div className="year-overview-legend-scale">
            {["#F4F6F8", "#fff8e6", "#ffc107", "#ffb300"].map((color) => (
              <span
                key={color}
                className="year-overview-legend-box"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <span className="year-overview-legend-label">More</span>
        </div>
      </div>
    </div>
  );
};

export default YearOverviewCard;
